// IMPORTS
import axios from 'axios';

// Navigation imports
import NavbarContainer from './src/js/containers/navigation/NavbarContainer';
import AlertsNotificationContainer from './src/js/containers/navigation/AlertsNotificationContainer';
import SearchInputContainer from './src/js/containers/navigation/SearchInputContainer';
import Footer from './src/js/components/navigation/footer/Footer';
import AccountPanel from './src/js/components/navigation/account/AccountPanel';
import ConfirmPasswordModal from './src/js/components/navigation/account/confirmPassword/ConfirmPasswordModal';
import SignInModal from './src/js/components/navigation/account/signIn/SignInModal';
import RequireLogin from './src/js/containers/RequireLogin';
import MobileAuth from './src/js/containers/MobileAuth';

// Reducer imports
import sarsaparillaReducers from './src/js/reducers';
import searchCriteriaReducers from './src/js/reducers/search/searchCriteriaReducers';
import searchSuggestionsReducers from './src/js/reducers/search/searchSuggestionsReducers';

// Action imports
import * as AlertActions from './src/js/actions/navigation/alertActions';
import * as CartActions from './src/js/actions/navigation/cartActions';
import * as LoginActions from './src/js/actions/navigation/loginActions';
import * as SearchActions from './src/js/actions/navigation/searchActions';
import * as SessionFeatureFlags from './src/js/actions/featureflags/SessionFeatureFlags';
import * as RecaptchaActions from './src/js/actions/recaptcha/recaptchaActions';
import * as NotificationActions from './src/js/actions/notification/notificationActions';
import * as RecommendationActions from './src/js/actions/recommendations/recommendationActions';

// Store
import store from './src/store';

// Common PropTypes
import * as TYPES from './src/js/types';

// Style Guide Imports
import AvailabilityTypes from './src/js/components/sharedComponents/AvailabilityTypes';
import BlockTabs from './src/js/components/sharedComponents/BlockTabs';
import Breadcrumbs from './src/js/components/sharedComponents/Breadcrumbs';
import Pagination from './src/js/components/sharedComponents/Pagination';
import CartTimer from './src/js/components/sharedComponents/CartTimer';
import ContentSwitcher from './src/js/components/sharedComponents/ContentSwitcher';
import Label, { VisualOnlyLabel } from './src/js/components/sharedComponents/Label';
import Dropdown from './src/js/components/sharedComponents/Dropdown/Dropdown';
import * as Icons from './src/js/components/sharedComponents/icons/Icons';
import * as BrandingIcons from './src/js/components/sharedComponents/icons/branding/BrandingIcons';
import * as CardIcons from './src/js/components/sharedComponents/icons/creditCards/CreditCardIcons';
import * as PartnerIcons from './src/js/components/sharedComponents/icons/partners/PartnerIcons';
import * as PartnerColorIcons from './src/js/components/sharedComponents/icons/partnersColor/PartnerColorIcons';
import * as InventoryTag from './src/js/components/sharedComponents/inventoryTags/InventoryTag';
import * as BrowserIcons from './src/js/components/sharedComponents/icons/browsers/BrowserIcons';
import * as AppIcons from './src/js/components/sharedComponents/icons/app/AppIcons';
import * as NewInventoryIcons from './src/js/components/sharedComponents/icons/newInventory/NewInventoryIcons';
import * as ChristmasTreeIcons from './src/js/components/sharedComponents/icons/christmasTree/ChristmasTreeIcons';
import * as StatusIndicators from './src/js/components/sharedComponents/status/Status';
import Loading from './src/js/components/sharedComponents/Loading';
import NavBar from './src/js/components/sharedComponents/header/Header';
import Panel from './src/js/components/sharedComponents/Panel';
import ProgressIndicator from './src/js/components/sharedComponents/ProgressIndicator';
import SubNav from './src/js/components/sharedComponents/header/Subhead';
import Tabs from './src/js/components/sharedComponents/Tabs';
import Slider from './src/js/components/sharedComponents/Slider';
import Gallery from './src/js/components/sharedComponents/Gallery';
import MediaGallery from './src/js/components/sharedComponents/MediaGallery';
import ImageUnavailable from './src/js/components/sharedComponents/ImageUnavailable';
import InventoryImage from './src/js/components/sharedComponents/InventoryImage';
import HeroImage from './src/js/components/sharedComponents/HeroImage';
import HeroImageCombined from './src/js/components/sharedComponents/HeroImageCombined';
import HeroImageSectionFront from './src/js/components/sharedComponents/HeroImageSectionFront';
import SvgLoading from './src/js/components/sharedComponents/SvgLoading';
import HeroImageUnavailable from './src/js/components/sharedComponents/HeroImageUnavailable';
import HeroImageLowQuality from './src/js/components/sharedComponents/HeroImageLowQuality';
import {
    DateRangePicker,
    DateRangePickerWithKey,
    DateRangePickerWithQuantities,
    DayPickerRangeController,
    DayPickerRangeControllerWithKey,
    SingleDatePicker,
    SingleDatePickerWithKey,
    SingleDatePickerWithQuantities,
    DayPickerSingleDateController,
    DayPickerSingleDateControllerWithKey
} from './src/js/components/sharedComponents/DatePickers';
import EditableWrapper from './src/js/components/sharedComponents/editableWrapper/EditableWrapper';
import DisplayTemplate from './src/js/components/sharedComponents/editableWrapper/DisplayTemplate';
import EditTemplate from './src/js/components/sharedComponents/editableWrapper/EditTemplate';
import Counter from './src/js/components/sharedComponents/guestCounter/Counter';
import GuestCounter from './src/js/components/sharedComponents/guestCounter/GuestCounter';
import GuestCounterRow from './src/js/components/sharedComponents/guestCounter/GuestCounterRow';
import PopupContent from './src/js/components/sharedComponents/map/PopupContent';
import CampsiteOrderItem from './src/js/components/sharedComponents/internalAccounts/orderSummary/CampsiteOrderItem';
import TourOrderItem from './src/js/components/sharedComponents/internalAccounts/orderSummary/TourOrderItem';
import OrderPriceItem from './src/js/components/sharedComponents/internalAccounts/orderSummary/OrderPriceItem';
import ModifiedOrderItem from './src/js/components/sharedComponents/internalAccounts/orderSummary/ModifiedOrderItem';
import ExistingOrderItem from './src/js/components/sharedComponents/internalAccounts/orderSummary/ExistingOrderItem';
import PhotoManagementComponent from './src/js/components/sharedComponents/photoManagement/PhotoManagementComponent';
import Modal from './src/js/components/sharedComponents/Modal';
import NotFoundPage from './src/js/components/navigation/NotFoundPage';
import ComingSoonPage from './src/js/components/navigation/ComingSoonPage';
import LegacyToolTip from './src/js/components/sharedComponents/LegacyToolTip';
import AvailabilityCell from './src/js/components/sharedComponents/tables/AvailabilityCell';
import SortableTableColumnHeader from './src/js/components/sharedComponents/tables/SortableTableColumnHeader';
import PasswordStrengthIndicator from './src/js/components/sharedComponents/PasswordStrengthIndicator';
import ValidationModal from './src/js/components/sharedComponents/ValidationModal';
import HelmetWrapper from './src/js/components/sharedComponents/HelmetWrapper';
import SingleClickButton from './src/js/components/sharedComponents/SingleClickButton';
import StickyBottomHelper from './src/js/components/sharedComponents/StickyBottomHelper';
import Announcement from './src/js/components/sharedComponents/Announcement';
import OrderConfirmationPage from './src/js/components/sharedComponents/orderDetails/OrderConfirmationPage';
import VenuePage from './src/js/components/sharedComponents/VenuePage';
import QueryRouter from './src/js/components/sharedComponents/QueryRouter';

// Helpers
import * as AlertLevelsHelper from './src/js/helpers/alertLevelsHelper';
import * as CartHelper from './src/js/helpers/cartHelper';
import * as DateHelper from './src/js/helpers/dateHelper';
import * as LoggingHelper from './src/js/helpers/loggingHelper';
import * as PageViewHelper from './src/js/helpers/pageViewHelper';
import * as MapHelper from './src/js/helpers/mapHelper';
import * as ScrollToHelper from './src/js/helpers/scrollToHelper';
import * as SecurityHelper from './src/js/helpers/securityHelper';
import * as StateHelper from './src/js/helpers/stateHelper';
import * as StringHelper from './src/js/helpers/stringHelper';
import announce from './src/js/helpers/announce';
import { formatMoney } from './src/js/helpers/moneyHelper';
import * as URLHelper from './src/js/helpers/urlHelper';
import SessionStorage from './src/js/helpers/storage/SessionStorage';
import LocalStorage from './src/js/helpers/storage/LocalStorage';
import * as BrowserHelper from './src/js/helpers/browserHelper';
import setMaxHeightAndPosition from './src/js/docs/utils/setMaxHeightAndPosition';
import { loadQualtrics } from './src/js/helpers/qualtricsHelper';
import formatNumber from './src/js/helpers/formatNumber';
import getFooterVisibilityByLocation from './src/js/helpers/getFooterVisibilityByLocation';

// UI Shared Components
import Carousel from './src/js/components/sharedComponents/Carousel';
import AccountSubmenu from './src/js/components/navigation/account/AccountSubmenu';
import BackToTop from './src/js/components/sharedComponents/BackToTop';
import ErrorReporting from './src/js/components/sharedComponents/ErrorReporting';
import SuggestPass from './src/js/components/sharedComponents/SuggestPass';
import NeedToKnow from './src/js/components/sharedComponents/NeedToKnow';
import FavoriteHeart from './src/js/components/sharedComponents/FavoriteHeart';
import { ExternalLink } from './src/js/components/sharedComponents/Link';
import ExternalVerifyContactInfo from './src/js/components/sharedComponents/verifycontact/ExternalVerifyContactInfo';
import InternalVerifyContactInfo from './src/js/components/sharedComponents/verifycontact/InternalVerifyContactInfo';
import DisplayHtmlContent from './src/js/components/sharedComponents/DisplayHtmlContent';
import FlexPageBody from './src/js/components/sharedComponents/LayoutHelpers/FlexPageBody';
import ContentContainer from './src/js/components/sharedComponents/LayoutHelpers/ContentContainer';
import SingleColumnContainer from './src/js/components/sharedComponents/LayoutHelpers/SingleColumnContainer';
import ContentContainerBodyShift from './src/js/components/sharedComponents/LayoutHelpers/ContentContainerBodyShift';
import FlexRow from './src/js/components/sharedComponents/FlexGrid/FlexRow';
import FlexCol from './src/js/components/sharedComponents/FlexGrid/FlexCol';
import FlexColBreak from './src/js/components/sharedComponents/FlexGrid/FlexColBreak';
import FlexContainer from './src/js/components/sharedComponents/FlexGrid/FlexContainer';
import NoMapboxGlNotice from './src/js/components/sharedComponents/NoMapboxGlNotice';
import ToggleSwitch from './src/js/components/sharedComponents/ToggleSwitch';
import Recaptcha from './src/js/components/sharedComponents/recaptcha/Recaptcha';
import Recaptchav3 from './src/js/components/sharedComponents/recaptcha/RecaptchaV3';
import * as recaptchav3Lib from './src/js/components/sharedComponents/recaptcha/recaptchav3Lib';
import IncompatibleBrowserNotice from './src/js/components/sharedComponents/IncompatibleBrowserNotice'
import TextField, { TextFieldStateless, PhoneNumberField, ZIPCodeField, EmailField, BirthdateField } from './src/js/components/sharedComponents/TextField';
import RichTextField from './src/js/components/sharedComponents/RichTextField'
import Select, { SelectOption, CountryPicker, StatePicker } from './src/js/components/sharedComponents/Select';
import Button, { ButtonGroup } from './src/js/components/sharedComponents/Button';
import Heading from './src/js/components/sharedComponents/Heading';
import NotificationContainer from './src/js/containers/notification/NotificationContainer';
import Notification from './src/js/components/sharedComponents/Notification';
import Checkbox from './src/js/components/sharedComponents/Checkbox';
import Radio, { RadioGroup } from './src/js/components/sharedComponents/Radio';
import ScrollableList, { ScrollableListItem } from './src/js/components/sharedComponents/ScrollableList';
import Tooltip, { TooltipPopup } from './src/js/components/sharedComponents/Tooltip';
import HelpTip from './src/js/components/sharedComponents/HelpTip';
import Badge from './src/js/components/sharedComponents/Badge';
import DropdownMenu from './src/js/components/sharedComponents/DropdownMenu';
import DropdownBase from './src/js/components/sharedComponents/DropdownBase';
import Accordion, { AccordionItem, AccordionHeading, AccordionContent } from './src/js/components/sharedComponents/Accordion';
import AndroidAppDownloadBanner from "./src/js/components/AndroidAppDownloadBanner";
import FocusManager from './src/js/components/sharedComponents/FocusManager';
import NumberField from './src/js/components/sharedComponents/NumberField';
import TextArea from './src/js/components/sharedComponents/TextArea';
import { SkipNavLink, SkipNavContent } from './src/js/components/sharedComponents/SkipNav';
import Drawer from './src/js/components/sharedComponents/Drawer';
import ScreenReaderTip from './src/js/components/sharedComponents/ScreenReaderTip';
import Spinner from './src/js/components/sharedComponents/Spinner';
import FormValidationAlert from './src/js/components/sharedComponents/FormValidationAlert';
import Fieldset from './src/js/components/sharedComponents/Fieldset';
import ReservationHistory from './src/js/components/sharedComponents/reservationHistory/ReservationHistory';
import { StickyTableWrap, StickyTableHorizontalScroller, StickyTableStickyStuff } from './src/js/components/sharedComponents/StickyTable';
import ErrorBoundary from './src/js/components/sharedComponents/ErrorBoundary';
import Recommendations from './src/js/components/recommendations/Recommendations';
import ProtectedRoute, { InternalProtectedRoute } from './src/js/components/sharedComponents/ProtectedRoute';
import RestoreScroll from './src/js/components/sharedComponents/RestoreScroll';

// Maps
import * as mapUtils from './src/js/components/sharedComponents/map/mapUtils';
import MapLayerControl from './src/js/components/sharedComponents/map/MapLayerControl';
import MapFullscreenControl from './src/js/components/sharedComponents/map/MapFullscreenControl';
import MapSearchOnMoveControl from './src/js/components/sharedComponents/map/MapSearchOnMoveControl';

// Validations
import CompareValidation from './src/js/components/validation/CompareValidation';
import CustomValidation from './src/js/components/validation/CustomValidation';
import EmailValidation from './src/js/components/validation/EmailValidation';
import DateRangeValidation from './src/js/components/validation/DateRangeValidation';
import LatitudeValidation from './src/js/components/validation/LatitudeValidation';
import LongitudeValidation from './src/js/components/validation/LongitudeValidation';
import PhoneValidation from './src/js/components/validation/PhoneValidation';
import RangeValidation from './src/js/components/validation/RangeValidation';
import RegexValidation from './src/js/components/validation/RegexValidation';
import RequiredValidation from './src/js/components/validation/RequiredValidation';
import ValidationMessage from './src/js/components/validation/ValidationMessage';
import {
    phoneValidator,
    emailValidator,
    phoneUSOnlyValidator,
    zipCodeValidator
} from './src/js/helpers/validators';

import Notice from './src/js/components/notifications/Notice';
import NotificationsWrapper from './src/js/components/notifications/NotificationsWrapper';
import NotificationToast from './src/js/components/notifications/notificationTypes/NotificationToast';

// EXPORTS

const { sluggify, toTitleCase, pluralize } = StringHelper;

axios.defaults.headers.common.Pragma = 'no-cache';
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';

export { NavbarContainer };
export { AlertsNotificationContainer };
export { SearchInputContainer };
export { Footer };
export { AccountPanel };
export { ConfirmPasswordModal };
export { SignInModal };
export { RequireLogin };
export { MobileAuth };


export { sarsaparillaReducers };
export { searchCriteriaReducers };
export { searchSuggestionsReducers };

export { store };
export { StickyBottomHelper };
export { AlertActions };
export { AvailabilityTypes };
export { CartActions };
export { LoginActions };
export { SearchActions };
export { SessionFeatureFlags };
export { RecaptchaActions };
export { Recommendations };
export { RecommendationActions };
export { NotificationActions };

export { BlockTabs };
export { Breadcrumbs };
export { Pagination };
export { CartTimer };
export { ContentSwitcher };
export { Label, VisualOnlyLabel };
export { Dropdown };
export { Icons };
export { BrandingIcons };
export { CardIcons };
export { PartnerIcons };
export { PartnerColorIcons };
export { BrowserIcons };
export { AppIcons };
export { NewInventoryIcons };
export { ChristmasTreeIcons };
export { StatusIndicators };
export { InventoryTag };
export { Loading };
export { NavBar };
export { Panel };
export { ProgressIndicator };
export { SubNav };
export { Tabs };
export { Slider };
export { Gallery };
export { MediaGallery };
export { ImageUnavailable };
export { InventoryImage };
export { HeroImage };
export { HeroImageCombined };
export { SvgLoading };
export { HeroImageUnavailable };
export { HeroImageLowQuality };
export { HeroImageSectionFront };
export {
    DateRangePicker,
    DateRangePickerWithKey,
    DateRangePickerWithQuantities,
    DayPickerRangeController,
    DayPickerRangeControllerWithKey,
    SingleDatePicker,
    SingleDatePickerWithKey,
    SingleDatePickerWithQuantities,
    DayPickerSingleDateController,
    DayPickerSingleDateControllerWithKey
};
export { EditableWrapper };
export { DisplayTemplate };
export { EditTemplate };
export { Counter };
export { GuestCounter };
export { GuestCounterRow };
export { PopupContent };
export { CampsiteOrderItem };
export { TourOrderItem };
export { OrderPriceItem };
export { ModifiedOrderItem };
export { ExistingOrderItem };
export { PhotoManagementComponent };
export { Modal };
export { LegacyToolTip };
export { AvailabilityCell };
export { SortableTableColumnHeader };
export { PasswordStrengthIndicator };
export { ValidationModal };
export { HelmetWrapper };
export { SingleClickButton };
export { Announcement };
export { DisplayHtmlContent };
export { OrderConfirmationPage };
export { VenuePage };

export { FlexRow };
export { FlexCol };
export { FlexContainer };
export { FlexColBreak };
export { SingleColumnContainer };
export { ContentContainerBodyShift };
export { FlexPageBody };
export { ContentContainer };

export { RichTextField };

export { AlertLevelsHelper };
export { CartHelper };
export { DateHelper };
export { LoggingHelper };
export { MapHelper };
export { PageViewHelper };
export { ScrollToHelper };
export { SecurityHelper };
export { StateHelper };
export { StringHelper, sluggify, toTitleCase, pluralize };
export { announce };
export { formatMoney };
export { URLHelper };
export { NotFoundPage };
export { ComingSoonPage };
export { LocalStorage };
export { SessionStorage };
export { BrowserHelper };
export { setMaxHeightAndPosition };
export { loadQualtrics };

export { Carousel };
export { AccountSubmenu };
export { BackToTop };
export { ErrorReporting };
export { SuggestPass };
export { NeedToKnow };
export { FavoriteHeart };
export { ExternalLink };
export { ExternalVerifyContactInfo }
export { InternalVerifyContactInfo }

export { mapUtils };
export { MapLayerControl };
export { MapFullscreenControl };
export { MapSearchOnMoveControl };
export { NoMapboxGlNotice };

export { CompareValidation };
export { CustomValidation };
export { EmailValidation };
export { DateRangeValidation };
export { LatitudeValidation };
export { LongitudeValidation };
export { PhoneValidation };
export { RangeValidation };
export { RegexValidation };
export { RequiredValidation };
export { ValidationMessage };
export {
    phoneValidator,
    emailValidator,
    phoneUSOnlyValidator,
    zipCodeValidator
};
export { Notice };
export { NotificationsWrapper };
export { NotificationToast };
export { ToggleSwitch };
export { Recaptcha };
export { Recaptchav3 };
export { recaptchav3Lib };
export { IncompatibleBrowserNotice };
export { TextField, TextFieldStateless, PhoneNumberField, ZIPCodeField, EmailField, BirthdateField };
export { Select, SelectOption, CountryPicker, StatePicker };
export { Button, ButtonGroup };
export { Notification, NotificationContainer };
export { Checkbox };
export { Radio, RadioGroup };
export { ScrollableList, ScrollableListItem };
export { Heading };
export { Tooltip, TooltipPopup };
export { HelpTip };
export { Badge };
export { Accordion, AccordionItem, AccordionHeading, AccordionContent };
export { AndroidAppDownloadBanner };
export { DropdownMenu, DropdownBase };
export { FocusManager };
export { NumberField };
export { TextArea };
export { SkipNavLink, SkipNavContent };
export { Drawer };
export { ScreenReaderTip };
export { Spinner };
export { FormValidationAlert };
export { Fieldset };
export { ReservationHistory };
export { ErrorBoundary };
export { ProtectedRoute, InternalProtectedRoute };
export { formatNumber };
export { getFooterVisibilityByLocation };
export {
    StickyTableWrap,
    StickyTableHorizontalScroller,
    StickyTableStickyStuff
};
export {
    Cell,
    Divider,
    LeftRail,
    PageBody,
    PageLayout,
    PageSection,
    RightRail,
    Row
} from './src/js/components/sharedComponents/layout';
export { TYPES };
export { QueryRouter }
export { RestoreScroll }